import React, { useState, useEffect, useRef } from 'react';
import { Clock, PauseCircle, AlertOctagon } from 'lucide-react';

/* Format minutes into readable string */
function fmtMins(mins) {
  if (mins <= 0) return '00:00:00';
  const d = Math.floor(mins / (60 * 24));
  const h = Math.floor((mins % (60 * 24)) / 60);
  const m = Math.floor(mins % 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/* Format a live MM:SS countdown for <1 hour */
function fmtLive(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

/* Determine color from remaining fraction */
function getColor(remainingMins, totalMins) {
  if (!totalMins || totalMins <= 0) return '#6b7280';
  const pct = remainingMins / totalMins;
  if (pct > 0.5)  return '#16a34a';
  if (pct > 0.25) return '#d97706';
  return '#dc2626';
}

const SLA_TOTAL_HOURS = { critical: 4, high: 8, medium: 24, low: 48 };

export default function SLATimer({ ticketId, priority, compact = false, className = '' }) {
  const [sla, setSla]   = useState(null);
  const [live, setLive] = useState(0); // countdown seconds for <1h display
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const token = sessionStorage.getItem('itsm_token');
      const r = await fetch(`/api/tickets/${ticketId}/sla`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const data = await r.json();
      setSla(data);
      // Seed live seconds for sub-hour countdown
      if (!data.is_paused && !data.is_breached && data.remaining_minutes < 60) {
        setLive(data.remaining_minutes * 60);
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!ticketId) return;
    load();
    // Refresh SLA data every 2 minutes
    const poll = setInterval(load, 120_000);
    return () => clearInterval(poll);
  // eslint-disable-next-line
  }, [ticketId]);

  // Live countdown ticker for <60 min
  useEffect(() => {
    if (!sla || sla.is_paused || sla.is_breached || sla.remaining_minutes >= 60) {
      clearInterval(timerRef.current);
      return;
    }
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setLive(prev => {
        if (prev <= 1) { load(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line
  }, [sla]);

  if (!sla || sla.no_sla) return null;

  const totalMins  = (SLA_TOTAL_HOURS[sla.priority] || 24) * 60;
  const color      = sla.is_breached ? '#dc2626' : sla.is_paused ? '#6b7280' : getColor(sla.remaining_minutes, totalMins);
  const bgColor    = color + '18';

  if (sla.is_breached) {
    return (
      <span className={className} style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: compact ? '2px 8px' : '4px 10px',
        borderRadius: 8, background: '#fee2e2', color: '#dc2626',
        fontSize: compact ? 11 : 12, fontWeight: 700,
        border: '1.5px solid #fca5a5', animation: 'sla-pulse 1.5s ease-in-out infinite',
      }}>
        <AlertOctagon size={compact ? 11 : 13} />
        SLA BREACH
      </span>
    );
  }

  if (sla.is_paused) {
    return (
      <span className={className} style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: compact ? '2px 8px' : '4px 10px',
        borderRadius: 8, background: '#f3f4f6', color: '#6b7280',
        fontSize: compact ? 11 : 12, fontWeight: 600,
        border: '1.5px solid #d1d5db',
      }}>
        <PauseCircle size={compact ? 11 : 13} />
        SLA PAUSED
      </span>
    );
  }

  const displayText = sla.remaining_minutes < 60
    ? fmtLive(live)
    : fmtMins(sla.remaining_minutes);

  return (
    <span className={className} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: compact ? '2px 8px' : '4px 10px',
      borderRadius: 8, background: bgColor, color,
      fontSize: compact ? 11 : 12, fontWeight: 700,
      border: `1.5px solid ${color}40`,
      fontFamily: sla.remaining_minutes < 60 ? 'var(--font-mono, monospace)' : 'inherit',
    }}>
      <Clock size={compact ? 11 : 13} />
      {displayText}
    </span>
  );
}
